import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "./store";

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;   // 15 minutes
const WARN_AT_MS      = 14 * 60 * 1000;   // pop warning 1 min before
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "touchstart", "scroll"] as const;

/**
 * Hook mounted once at the protected-shell level. Resets a timer on
 * any user activity. When the user is idle for {@link IDLE_TIMEOUT_MS},
 * we wipe the session and bounce them to /login. A modal warning fires
 * 1 minute earlier so a working admin can extend the session.
 */
export function useIdleTimer() {
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const recordActivity = useAuthStore((s) => s.recordActivity);
  const [showWarning, setShowWarning] = useState(false);
  const warnTimer = useRef<number | null>(null);
  const logoutTimer = useRef<number | null>(null);

  function resetTimers() {
    if (warnTimer.current !== null) window.clearTimeout(warnTimer.current);
    if (logoutTimer.current !== null) window.clearTimeout(logoutTimer.current);
    setShowWarning(false);
    warnTimer.current = window.setTimeout(() => setShowWarning(true), WARN_AT_MS);
    logoutTimer.current = window.setTimeout(() => {
      clear();
      navigate("/login?reason=idle", { replace: true });
    }, IDLE_TIMEOUT_MS);
  }

  useEffect(() => {
    function onActivity() {
      recordActivity();
      resetTimers();
    }
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    resetTimers();
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      if (warnTimer.current !== null) window.clearTimeout(warnTimer.current);
      if (logoutTimer.current !== null) window.clearTimeout(logoutTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    showWarning,
    dismissWarning: () => {
      setShowWarning(false);
      resetTimers();
    },
    logoutNow: () => {
      clear();
      navigate("/login", { replace: true });
    },
  };
}
